import React, { useState, useEffect } from 'react';
import adminApiService from '../../services/adminApiService';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getDashboard();
      
      if (response.success) {
        setDashboardData(response);
        console.log('👑 Admin Dashboard loaded:', response);
      } else {
        setError(response.message || 'Failed to load dashboard');
      }
    } catch (error) {
      console.error('❌ Error loading admin dashboard:', error);
      setError(error.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations
  const createChartData = (labels, data, colors) => ({
    labels: Array.from(labels || []),
    datasets: [{
      data: Array.from(data || []),
      backgroundColor: colors,
      hoverBackgroundColor: colors.map(color => color.replace('0.8', '1')),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  });

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '70%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1
      }
    }
  };

  const bookedChartColors = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e'];
  const cancelledChartColors = ['#e74a3b', '#f6c23e', '#36b9cc', '#8e44ad'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <i className="fas fa-exclamation-triangle fa-3x text-red-500 mb-3"></i>
        <h4 className="text-xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h4>
        <p className="text-gray-600 mb-4">{error}</p>
        <button 
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors"
          onClick={loadDashboard}
        >
          <i className="fas fa-redo mr-2"></i> Try Again
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <i className="fas fa-info-circle fa-3x text-yellow-500 mb-3"></i>
        <h4 className="text-xl font-bold text-gray-900 mb-2">No Data Available</h4>
        <p className="text-gray-600 mb-4">Dashboard data is not available at the moment.</p>
        <button 
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
          onClick={loadDashboard}
        >
          <i className="fas fa-sync mr-2"></i> Refresh
        </button>
      </div>
    );
  }

  const {
    stats,
    last12MonthBookings,
    last12MonthCancellations,
    agentCount,
    totalTickets,
    currentMonthStats,
    amountStats,
    monthlyReport,
    todaySummary,
    providerNameBooked,
    providerBookingCountBooked,
    providerLabelsBooked,
    providerCountsBooked,
    providerNameCancelled,
    providerBookingCountCancelled,
    providerLabelsCancelled,
    providerCountsCancelled,
    fundSummary,
    platformFee,
    monthlyPlatformFee
  } = dashboardData;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Comprehensive overview of your hotel booking system</p>
        </div>

        {/* Stats Cards Row 1 - Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Agents */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 mb-1">Total Agents</p>
                <p className="text-2xl font-bold text-gray-900">{agentCount || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <i className="fas fa-users text-blue-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Total Support Tickets */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Support Tickets</p>
                <p className="text-2xl font-bold text-gray-900">{totalTickets || 0}</p>
              </div>
              <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                <i className="fas fa-clipboard-list text-gray-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Total Bookings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 mb-1">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalBookings || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <i className="fas fa-calendar-check text-green-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Total Cancellations */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600 mb-1">Total Cancellation</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalCancellations || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <i className="fas fa-times-circle text-red-600 text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards Row 2 - 12 Months & Current Stats (Colored Backgrounds) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Last 12 Months Total Booking */}
          <div className="bg-blue-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">Last 12 Months Booking</p>
                <p className="text-2xl font-bold">{last12MonthBookings || 0}</p>
              </div>
              <i className="fas fa-chart-bar text-blue-200 text-2xl"></i>
            </div>
          </div>

          {/* Last 12 Months Total Cancellation */}
          <div className="bg-yellow-500 text-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm mb-1">Last 12 Months Cancellation</p>
                <p className="text-2xl font-bold">{last12MonthCancellations || 0}</p>
              </div>
              <i className="fas fa-clipboard-list text-yellow-200 text-2xl"></i>
            </div>
          </div>

          {/* Current Month Bookings */}
          <div className="bg-green-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm mb-1">Current Month Bookings</p>
                <p className="text-2xl font-bold">{currentMonthStats?.totalCurrentMonthBookings || 0}</p>
              </div>
              <i className="fas fa-calendar-check text-green-200 text-2xl"></i>
            </div>
          </div>

          {/* Current Month Cancellations */}
          <div className="bg-red-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm mb-1">Current Month Cancellation</p>
                <p className="text-2xl font-bold">{currentMonthStats?.totalCurrentMonthCancellations || 0}</p>
              </div>
              <i className="fas fa-times-circle text-red-200 text-2xl"></i>
            </div>
          </div>
        </div>

        {/* Stats Cards Row 3 - Amount Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 border-l-4 border-l-blue-500">
            <p className="text-sm font-medium text-gray-500 mb-1">12 Month Booking Amt</p>
            <p className="text-xl font-bold text-gray-900">{amountStats?.formattedLast12MonthsBookingAmount || '₹0.00'}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 border-l-4 border-l-yellow-500">
            <p className="text-sm font-medium text-gray-500 mb-1">12 Month Refund Amt</p>
            <p className="text-xl font-bold text-gray-900">{amountStats?.formattedLast12MonthsRefundAmount || '₹0.00'}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 border-l-4 border-l-green-500">
            <p className="text-sm font-medium text-gray-500 mb-1">Current Mth Booking Amt</p>
            <p className="text-xl font-bold text-gray-900">{amountStats?.formattedCurrentMonthBookingAmount || '₹0.00'}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 border-l-4 border-l-red-500">
            <p className="text-sm font-medium text-gray-500 mb-1">Current Mth Refund Amt</p>
            <p className="text-xl font-bold text-gray-900">{amountStats?.formattedCurrentMonthRefundAmount || '₹0.00'}</p>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column (1/3) */}
          <div className="lg:col-span-1 space-y-6">
            {/* Today's Report */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  <i className="fas fa-calendar-day mr-2"></i>
                  Today's Reports {todaySummary?.todayDate || ''}
                </h3>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-calendar-check text-blue-600 mr-3"></i>
                      <span className="text-sm font-medium text-gray-700">Total Booking</span>
                    </div>
                    <span className="text-lg font-bold text-blue-600">{todaySummary?.totalTodayBookings || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-coins text-green-600 mr-3"></i>
                      <span className="text-sm font-medium text-gray-700">Booking Amount</span>
                    </div>
                    <span className="text-lg font-bold text-green-600">{todaySummary?.formattedTodayBookingAmount || '₹0.0'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-times-circle text-red-600 mr-3"></i>
                      <span className="text-sm font-medium text-gray-700">Cancellation</span>
                    </div>
                    <span className="text-lg font-bold text-red-600">{todaySummary?.totalTodayCancellations || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-undo text-yellow-600 mr-3"></i>
                      <span className="text-sm font-medium text-gray-700">Refund Amount</span>
                    </div>
                    <span className="text-lg font-bold text-yellow-600">{todaySummary?.formattedTodayCancellationAmount || '₹0.0'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-ticket-alt text-purple-600 mr-3"></i>
                      <span className="text-sm font-medium text-gray-700">Ticket Generated</span>
                    </div>
                    <span className="text-lg font-bold text-purple-600">{todaySummary?.totalTodayTicketsGenerated || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-success text-white rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-medium mb-1"><i className="fas fa-wallet mr-2"></i>Total Funds Deposited this FY</h2>
              <p className="text-3xl font-bold">{fundSummary?.formattedFundDeposited || '₹0.00'}</p>
            </div>
            
            <div className="bg-cyan-600 text-white rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-medium mb-1"><i className="fas fa-calendar-alt mr-2"></i>Past 12 Months Platform Fees</h2>
              <p className="text-3xl font-bold">{platformFee?.formattedPlatformFee || '₹0.00'}</p>
            </div>

            <div className="bg-blue-600 text-white rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-medium mb-1"><i className="fas fa-chart-line mr-2"></i>Current Month Platform Fees</h2>
              <p className="text-3xl font-bold">{monthlyPlatformFee?.currentMonthFormattedPlatformFee || '₹0.00'}</p>
            </div>
          </div>

          {/* Right Column (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Monthly Progress Tracker */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  <i className="fas fa-chart-line mr-2"></i>
                  Monthly Progress Tracker
                </h3>
              </div>
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cancellation</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking Amt</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refund Amt</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform Fee</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {monthlyReport && monthlyReport.map((report, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{report.month}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{report.totalBookings}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{report.totalCancellations}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-700">{report.formattedBookingAmount}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-700">{report.formattedCancellationAmount}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-700">{report.formattedPlatformFee}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Provider Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    <i className="fas fa-chart-pie mr-2"></i>
                    Provider Data For Booking
                  </h3>
                </div>
                <div className="p-4">
                  <div className="h-64 mb-4">
                    {providerLabelsBooked && providerCountsBooked ? (
                      <Doughnut data={createChartData(providerLabelsBooked, providerCountsBooked, bookedChartColors)} options={chartOptions} />
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-500">No data available</div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {providerNameBooked && providerBookingCountBooked && 
                      providerNameBooked.map((label, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <div className="flex items-center">
                          <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: bookedChartColors[index % bookedChartColors.length] }}></span>
                          <span className="font-medium text-gray-700">{label}</span>
                        </div>
                        <span className="font-bold">{providerBookingCountBooked[index]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    <i className="fas fa-chart-pie mr-2"></i>
                    Provider Data For Cancellation
                  </h3>
                </div>
                <div className="p-4">
                  <div className="h-64 mb-4">
                    {providerLabelsCancelled && providerCountsCancelled ? (
                      <Doughnut data={createChartData(providerLabelsCancelled, providerCountsCancelled, cancelledChartColors)} options={chartOptions} />
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-500">No data available</div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {providerNameCancelled && providerBookingCountCancelled && 
                      providerNameCancelled.map((label, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <div className="flex items-center">
                          <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: cancelledChartColors[index % cancelledChartColors.length] }}></span>
                          <span className="font-medium text-gray-700">{label}</span>
                        </div>
                        <span className="font-bold">{providerBookingCountCancelled[index]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;